// Imported from its own file rather than the `utils` barrel, which would pull the app's runtime
// module graph into a constants module.
import { camelCaseToSentenceCase } from "../../utils/string/stringCase";
import { ProjectDatesConstants } from "./ProjectDates";

/**
 * Presentation metadata for a project date field, keyed by its flattened path
 * (e.g. `feasibility.start`). The date schema returned by the backend only
 * carries structure and types, so the label, tooltip and display ordering all
 * live here on the frontend and are merged into the schema before rendering.
 */
export interface DateFieldMetadata {
  label: string;
  tooltip?: string;
  /** Display order within the field's group. Lower renders first. */
  sequence: number;
}

/**
 * Metadata for every project date field, keyed by its flattened path. When a new
 * field is added to the date schema, add its metadata here to control the label,
 * tooltip and ordering; without an entry it still renders with a humanised label
 * derived from the key.
 *
 * ---
 * MIGRATING THIS TO THE SCHEMA (future work)
 *
 * This frontend store only exists because the date schema the backend returns currently carries
 * structure and types but no presentation metadata. The consuming component
 * (`DateSchemaFields.resolveDateFieldMetadata`) already resolves metadata **schema-first**, i.e. a
 * value on the schema property itself takes precedence and this store is only the fallback. The
 * generic renderer (`DynamicFormFieldsFromJsonSchema`) already reads `tooltip` and `sequence`
 * straight off each property, and `title` is now a recognised label keyword.
 *
 * So the migration is incremental and low-risk:
 *   1. Have the backend emit `title` (label), `tooltip` and `sequence` on each date-schema property.
 *   2. As each field/group gains schema metadata, delete its entry here — the schema value takes
 *      over automatically with no other code change.
 *   3. Once this record is empty, delete this file and the `getDateFieldMetadata` call in
 *      `DateSchemaFields` (the `normaliseSchemaFieldLabel` fallback already covers any gaps).
 */
export const PROJECT_DATE_FIELD_METADATA: Record<string, DateFieldMetadata> = {
  "feasibility.start": {
    label: "Phase start date",
    tooltip: ProjectDatesConstants.FEASIBILITY_START_TOOLTIP_TEXT,
    sequence: 1,
  },
  "feasibility.end": {
    label: "Phase end date",
    tooltip: ProjectDatesConstants.FEASIBILITY_END_TOOLTIP_TEXT,
    sequence: 2,
  },
  "design.start": {
    label: "Phase start date",
    tooltip: ProjectDatesConstants.DESIGN_START_TOOLTIP_TEXT,
    sequence: 1,
  },
  "design.end": {
    label: "Phase end date",
    tooltip: ProjectDatesConstants.DESIGN_END_TOOLTIP_TEXT,
    sequence: 2,
  },
  "design.registrationDate": {
    label: "Registration date",
    tooltip: ProjectDatesConstants.DESIGN_REGISTRATION_DATE_TOOLTIP_TEXT,
    sequence: 3,
  },
  "implementation.start": {
    label: "Phase start date",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_START_TOOLTIP_TEXT,
    sequence: 1,
  },
  "implementation.end": {
    label: "Phase end date",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_END_TOOLTIP_TEXT,
    sequence: 2,
  },
  "implementation.implementationDate": {
    label: "Project implementation date",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_IMPLEMENTATION_DATE_TOOLTIP_TEXT,
    sequence: 3,
  },
  "implementation.certificationDate": {
    label: "Certification date",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_CERTIFICATION_DATE_TOOLTIP_TEXT,
    sequence: 4,
  },
  "implementation.startDate": {
    label: "Project start date",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_START_DATE_TOOLTIP_TEXT,
    sequence: 5,
  },
  "implementation.validationDate": {
    label: "Validation date",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_FIRST_ASSESSMENT_DATE_TOOLTIP_TEXT,
    sequence: 6,
  },
  "implementation.restorationValidationDate": {
    label: "Restoration validation date",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_RESTORATION_VALIDATION_DATE_TOOLTIP_TEXT,
    sequence: 7,
  },
  "duration.duration": {
    label: "Project duration",
    tooltip: ProjectDatesConstants.IMPLEMENTATION_DURATION_TOOLTIP_TEXT,
    sequence: 1,
  },
  "ongoingMonitoring.overallStart": {
    label: "Phase start date",
    tooltip: ProjectDatesConstants.ONGOING_MONITORING_START_TOOLTIP_TEXT,
    sequence: 1,
  },
  "ongoingMonitoring.overallEnd": {
    label: "Phase end date",
    tooltip: ProjectDatesConstants.ONGOING_MONITORING_END_TOOLTIP_TEXT,
    sequence: 2,
  },
  "ongoingMonitoring.subsequentFrequency": {
    label: "Monitoring frequency",
    tooltip: ProjectDatesConstants.ONGOING_MONITORING_SUBSEQUENT_FREQUENCY_TOOLTIP_TEXT,
    sequence: 3,
  },
  "ongoingMonitoring.finalAssessmentDate": {
    label: "Final assessment date",
    tooltip: ProjectDatesConstants.ONGOING_MONITORING_FINAL_ASSESSMENT_DATE_TOOLTIP_TEXT,
    sequence: 4,
  },
};

/** Returns the presentation metadata for a date field, or undefined if none is configured. */
export const getDateFieldMetadata = (keyPrefix: string, fieldKey: string): DateFieldMetadata | undefined =>
  PROJECT_DATE_FIELD_METADATA[`${keyPrefix}.${fieldKey}`];

/**
 * The label for a date field: the configured one, else the key humanised. Every reader of project
 * dates — the edit panel, the overview and public details sections, and the roadmap — resolves labels
 * through here, so a field added to a standard's schema reads the same wherever it appears.
 */
export const getDateFieldLabel = (keyPrefix: string, fieldKey: string): string =>
  getDateFieldMetadata(keyPrefix, fieldKey)?.label ?? camelCaseToSentenceCase(fieldKey);
